/** Paths under `settings.outboundLabels` that feed into saved rates. */
const RATE_AFFECTING_SETTINGS: readonly (readonly string[])[] = [
  ["disabledRedoServiceLevels"],
  ["blockedServiceLevels"],
  ["carrierAccountDisabledServiceLevels"],
  ["blockedCarrierAccounts"],
  ["enabledCarrierAccounts"],
  ["useAmazonCarrierAccount"],
  ["useApcCarrierAccount"],
  ["useAustraliaPostCarrierAccount"],
  ["useDhlExpressCarrierAccount"],
  ["useEpostGlobalCarrierAccount"],
  ["uniUni"],
  ["dhlDirectIntegrationSettings"],
  ["competitiveSpreadSettings"],
  ["redoCompetitiveSpreadSettings"],
  ["documents", "shippingLabel", "includeOrderNumber"],
  ["documents", "shippingLabel", "includeSecondaryOrderName"],
  ["documents", "shippingLabel", "includeSkus"],
  ["documents", "shippingLabel", "includeCustomerId"],
  ["documents", "shippingLabel", "includeProductName"],
  ...[
    "customDate1",
    "customDate2",
    "customDate3",
    "customString1",
    "customString2",
    "customString3",
    "customNumber1",
    "customNumber2",
    "customNumber3",
  ].map((field) => ["customMetadata", field, "includeOnShippingLabel"]),
];

export interface RateRefreshTeam {
  settings?: { outboundLabels?: unknown } | null;
}

export function isOmsEnabled(team: unknown): boolean {
  return (
    getPath(team, ["settings", "outboundLabels", "newOmsEnabled"]) === true
  );
}

/**
 * Whether going from `oldTeam` to `newTeam` leaves saved rates stale.
 * Missing, null, false, and empty values count as the same, and `_id`s are ignored.
 */
export function shouldTriggerRateRefresh(
  oldTeam: RateRefreshTeam | null | undefined,
  newTeam: RateRefreshTeam | null | undefined,
): boolean {
  if (!isOmsEnabled(newTeam)) {
    return false;
  }
  const oldSettings = toJson(oldTeam?.settings?.outboundLabels);
  const newSettings = toJson(newTeam?.settings?.outboundLabels);
  return RATE_AFFECTING_SETTINGS.some(
    (path) =>
      !isEqual(
        normalize(getPath(oldSettings, path)),
        normalize(getPath(newSettings, path)),
      ),
  );
}

function toJson(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getPath(value: unknown, path: readonly string[]): unknown {
  let current = value;
  for (const key of path) {
    if (!isRecord(current)) {
      return undefined;
    }
    current = current[key];
  }
  return current;
}

function normalize(value: unknown): unknown {
  if (value == null || value === false) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(normalize) : undefined;
  }
  if (isRecord(value)) {
    const entries = Object.entries(value)
      .filter(([key]) => key !== "_id")
      .map(([key, item]) => [key, normalize(item)] as const)
      .filter(([, item]) => item !== undefined);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  }
  return value;
}

function isEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => isEqual(item, b[index]))
    );
  }
  if (isRecord(a) && isRecord(b)) {
    const keys = Object.keys(a);
    return (
      keys.length === Object.keys(b).length &&
      keys.every((key) => isEqual(a[key], b[key]))
    );
  }
  return a === b;
}
